import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useConfirm } from '@/hooks/useConfirm';
import type { AppBinding, AppBindingSession, ReachableSite } from '../api';
import { aliasLabel, platformLabel, timeLeftLabel } from '../format';
import {
  useAcknowledgeApp,
  useDisconnectApp,
  useExtendSite,
  useTurnSiteOff,
  useTurnSiteOn,
  useVpnSites,
} from '../hooks';
import { Card } from './Facts';
import { Switch } from '@/components/ui/switch';

/**
 * The controls for one run of the Witina app bound to you (docs/VpnAccess.md,
 * "Slice 9 design"): its state, the same-machine check, and a toggle for each
 * site your policies allow, by organization and then division. `code` is the
 * link's, when the app opened this page.
 */
export function AppControl({ binding, code }: { binding: AppBinding; code?: string }) {
  const acknowledge = useAcknowledgeApp(binding.id);
  const disconnect = useDisconnectApp();
  const { confirm, confirmDialog } = useConfirm();
  const { check } = binding;
  const mismatched = !check.addressMatched || !check.osMatched;
  const ended = binding.endedAt !== null;
  const usable = !ended && binding.connected && !check.needsAcknowledgement;

  return (
    <div className="space-y-5">
      <Card
        title={
          <span className="flex items-center gap-2">
            Witina VPN{binding.appVersion ? ` ${binding.appVersion}` : ''} on {binding.name}
            <Badge variant="outline" className="text-[10px]">
              {platformLabel(binding.platform)}
            </Badge>
            {ended ? null : (
              <Badge variant={binding.connected ? 'default' : 'secondary'} className="text-[10px]">
                {binding.connected ? 'Connected' : 'Not connected'}
              </Badge>
            )}
          </span>
        }
        description={
          code ? (
            <>
              Code <span className="font-mono">{code}</span>: it should match what your app shows. Only continue if
              your Witina VPN app opened this page; don&rsquo;t use a link anyone else gave you.
            </>
          ) : undefined
        }
        action={
          ended ? null : (
            <Button
              size="sm"
              variant="ghost"
              disabled={disconnect.isPending}
              onClick={() =>
                void (async () => {
                  const ok = await confirm({
                    title: `Disconnect Witina VPN on ${binding.name}?`,
                    message: 'Its sessions end now. Using it again means opening the app and its link again.',
                    isDanger: true,
                    confirmText: 'Disconnect',
                  });
                  if (ok) disconnect.mutate(binding.id);
                })()
              }
            >
              Disconnect
            </Button>
          )
        }
      >
        {ended ? (
          <p className="text-sm">This connection has ended. Open Witina VPN again for a new link.</p>
        ) : !binding.connected ? (
          <p className="text-sm">
            Witina VPN on {binding.name} isn&rsquo;t connected. Open it, and this page picks it up again.
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">
            Connected from {check.appAddresses.join(', ') || 'an unknown address'}.
          </p>
        )}
      </Card>

      {mismatched && !ended ? (
        <Alert variant="destructive" className="border-2">
          <AlertTriangle className="h-5 w-5" />
          <AlertTitle className="text-base">This page may not have been opened by your own app</AlertTitle>
          <AlertDescription className="space-y-2">
            {check.addressMatched ? null : (
              <p>
                The app is connecting from {check.appAddresses.join(', ') || 'an unknown address'}, and this browser from{' '}
                {check.browserAddress ?? 'an unknown address'}.
              </p>
            )}
            {check.osMatched ? null : (
              <p>
                The app runs on {platformLabel(binding.platform)}, and this browser on {platformLabel(check.browserOs)}.
              </p>
            )}
            <p>
              If someone sent you this link, close this page: turning a site on would connect their computer, as you.
              It can be fine behind a proxy or a browser VPN.
            </p>
            <label className="flex items-center gap-2 pt-1 font-medium">
              <Checkbox
                checked={!check.needsAcknowledgement}
                disabled={!check.needsAcknowledgement || acknowledge.isPending}
                onCheckedChange={(on) => {
                  if (on === true) acknowledge.mutate(undefined);
                }}
                aria-label="I opened this page from my own Witina VPN app"
              />
              I opened this page from my own Witina VPN app
            </label>
          </AlertDescription>
        </Alert>
      ) : null}

      {ended ? null : <Sites binding={binding} usable={usable} />}
      {confirmDialog}
    </div>
  );
}

function Sites({ binding, usable }: { binding: AppBinding; usable: boolean }) {
  const sites = useVpnSites();
  const turnOn = useTurnSiteOn(binding.id);
  const turnOff = useTurnSiteOff(binding.id);
  const extend = useExtendSite(binding.id);
  const live = new Map(binding.sessions.map((s) => [s.regionId, s]));
  // The site being turned on or off: its toggle shows where it's going, and
  // every toggle waits, until the answer is in.
  const [pending, setPending] = useState<{ regionId: string; on: boolean } | null>(null);
  const busy = pending !== null;

  if (sites.isLoading) return <p className="text-xs text-muted-foreground">Loading your sites…</p>;
  if (sites.isError) return <p className="text-xs text-muted-foreground">Couldn&rsquo;t load your sites.</p>;
  const organizations = sites.data ?? [];
  if (organizations.length === 0) {
    return (
      <Card title="Sites">
        <p className="text-xs text-muted-foreground">No VPN policy lets you connect anywhere yet. Ask an administrator.</p>
      </Card>
    );
  }

  const onToggle = (site: ReachableSite, on: boolean) => {
    const session = live.get(site.regionId);
    const settled = {
      onSettled: () => {
        setPending(null);
      },
    };
    if (on) {
      setPending({ regionId: site.regionId, on });
      turnOn.mutate(site.regionId, settled);
    } else if (session) {
      setPending({ regionId: site.regionId, on });
      turnOff.mutate(session.id, settled);
    }
  };

  return (
    <div className="space-y-5">
      {organizations.map((organization) => (
        <Card key={organization.id} title={organization.name}>
          <div className="space-y-4">
            {organization.divisions.map((division) => (
              <div key={division.id ?? 'none'}>
                {division.name ? (
                  <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">{division.name}</h3>
                ) : null}
                <ul className="divide-y divide-border">
                  {division.sites.map((site) => (
                    <SiteRow
                      key={site.regionId}
                      site={site}
                      session={live.get(site.regionId)}
                      turning={pending?.regionId === site.regionId ? pending.on : undefined}
                      disabled={!usable || busy}
                      onToggle={(on) => {
                        onToggle(site, on);
                      }}
                      onExtend={(session) => {
                        extend.mutate({ sessionId: session.id, minutes: site.maxSessionMinutes });
                      }}
                    />
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}

function SiteRow({
  site,
  session,
  turning,
  disabled,
  onToggle,
  onExtend,
}: {
  site: ReachableSite;
  session: AppBindingSession | undefined;
  /** Being turned on (true) or off (false), until the answer is in. */
  turning: boolean | undefined;
  disabled: boolean;
  onToggle: (on: boolean) => void;
  onExtend: (session: AppBindingSession) => void;
}) {
  const on = turning ?? session !== undefined;
  return (
    <li className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0">
      <Switch
        checked={on}
        disabled={disabled || (!on && !site.available)}
        onCheckedChange={onToggle}
        aria-label={site.regionName}
      />
      <div className="min-w-0 flex-1">
        <div className="text-sm truncate">{site.regionName}</div>
        <div className="text-xs text-muted-foreground">
          {turning !== undefined ? (
            turning ? (
              'Turning on…'
            ) : (
              'Turning off…'
            )
          ) : session ? (
            <>
              {session.startedAt ? 'Connected' : 'Connecting…'} · {timeLeftLabel(session.expiresAt)} left
              {session.skippedRoutes.length > 0 ? ` · skipped ${session.skippedRoutes.join(', ')} (clashes with your network)` : ''}
              {(session.ipv4Aliases ?? []).length > 0 ? (
                <div>{(session.ipv4Aliases ?? []).map(aliasLabel).join(' · ')}</div>
              ) : null}
            </>
          ) : site.available ? (
            'Off'
          ) : site.enabled ? (
            'No gateway can take a session right now'
          ) : (
            'The VPN is off at this site'
          )}
        </div>
      </div>
      {session ? (
        <Button
          size="sm"
          variant="ghost"
          disabled={disabled}
          onClick={() => {
            onExtend(session);
          }}
        >
          Extend
        </Button>
      ) : null}
    </li>
  );
}
